import {
  User2Icon,
  X,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Fingerprint,
  ShieldCheck,
  ArrowUpRight,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import React from "react";
import { Link } from "react-router-dom";

function ViewAdminProfile({ profileData, isOpen, onClose }) {
  if (!isOpen || !profileData) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-slate-400/20 backdrop-blur-sm"
        />

        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="relative w-full max-w-2xl bg-white border border-slate-200 rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.1)] overflow-hidden"
        >
          {/* Header Accent */}
          <div className="h-32 bg-gradient-to-br from-blue-50 to-blue-50 border-b border-slate-100" />

          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-2 bg-white shadow-sm border border-slate-100 rounded-full hover:bg-red-50 hover:text-red-500 transition-colors"
          >
            <X size={18} />
          </button>

          <div className="relative px-8 pb-10 -mt-16">
            <div className="flex flex-col md:flex-row items-end gap-6 mb-10">
              <div className="relative">
                <div className="w-32 h-32 rounded-3xl bg-white p-1.5 shadow-xl border border-slate-100">
                  <div className="w-full h-full rounded-[1.25rem] bg-gradient-to-r from-cyan-600 to-blue-800 flex items-center justify-center overflow-hidden">
                    <User2Icon className="w-14 h-14 text-gray-100" />
                  </div>
                </div>
                <span className="absolute bottom-2 right-2 h-5 w-5 bg-green-500 border-4 border-white rounded-full"></span>
              </div>

              <div className="flex-1 pb-2">
                <h2 className="text-2xl font-bold text-slate-900 capitalize">
                  {profileData.name}
                </h2>
                <p className="text-blue-600 font-medium">
                  {profileData.user_id}
                </p>
              </div>
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <DetailItem
                icon={<Mail />}
                label="Email Address"
                value={profileData.email_address}
              />
              <DetailItem
                icon={<Phone />}
                label="Mobile"
                value={profileData.mobile_number}
              />
              <DetailItem
                icon={<Fingerprint />}
                label="Aadhar"
                value={profileData.aadhar_number}
                isMasked
              />
              <DetailItem
                icon={<ShieldCheck />}
                label="PAN Number"
                value={profileData.pan_number}
                isMasked
              />
              <DetailItem
                icon={<Calendar />}
                label="Birthday"
                value={new Date(profileData.dob).toLocaleDateString()}
              />
              <DetailItem
                icon={<MapPin />}
                label="Location"
                value={`${profileData.district}, ${profileData.state}`}
              />
            </div>

            {/* Address Footer */}
            <div className="mt-10 p-5 bg-slate-50 rounded-2xl border border-slate-100">
              <p className="text-[11px] font-bold text-slate-400 uppercase mb-2 tracking-widest">
                Permanent Address
              </p>
              <p className="text-sm text-slate-600 leading-relaxed">
                {profileData.landmark_address}, {profileData.post_office},{" "}
                {profileData.district}, {profileData.state} -{" "}
                {profileData.pin_code}
              </p>
            </div>

            <div className="w-full flex justify-end mt-4">
              <Link
                to={"/profile"}
                onClick={onClose}
                className="text-sm flex justify-center items-center gap-2 bg-gradient-to-r from-cyan-600 to-blue-800  hover:from-green-500 hover:to-green-900 rounded-lg py-2 px-4 text-white "
              >
                <ArrowUpRight />
                Change Pin | Password
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

function DetailItem({ icon, label, value, isMasked }) {
  return (
    <div className="flex items-center gap-4">
      <div className="p-2.5 bg-blue-50 text-blue-500 rounded-xl">
        {React.cloneElement(icon, { size: 18 })}
      </div>
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider leading-none mb-1">
          {label}
        </p>
        <p className="text-sm font-semibold text-slate-700">
          {isMasked ? `•••• •••• ${value?.slice(-4)}` : value || "Not provided"}
        </p>
      </div>
    </div>
  );
}

export default ViewAdminProfile;
